import React from 'react';
import PropTypes from 'prop-types';

import {Box, Button, IconButton, Stack, Typography} from 'views/components';
import {ArrowForwardIos, InfoOutlined, ArrowBackIosNewRounded, CalendarIcon} from 'views/icons';
import {DatePicker, TimePicker, Translate, translate} from 'views/module';

import moment from 'moment';

const ScheduleEventEditRecurring = (props) => {
  const {initialEvent, onSave, onRemove} = props;

  const [startTime, setStartTime] = React.useState(null);
  const [endTime, setEndTime] = React.useState(null);
  const [startDate, setStartDate] = React.useState(null);
  const [endDate, setEndDate] = React.useState(null);
  const [repeats, setRepeats] = React.useState([]);

  const daysOfWeek = React.useMemo(() => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'], []);
  const dayShortLabels = {
    Sun: translate('schedulestep.Common.DayShort.Sun'),
    Mon: translate('schedulestep.Common.DayShort.Mon'),
    Tue: translate('schedulestep.Common.DayShort.Tue'),
    Wed: translate('schedulestep.Common.DayShort.Wed'),
    Thu: translate('schedulestep.Common.DayShort.Thu'),
    Fri: translate('schedulestep.Common.DayShort.Fri'),
    Sat: translate('schedulestep.Common.DayShort.Sat'),
  };

  const toMomentOrNull = React.useCallback((value) => {
    if (!value) {
      return null;
    }

    if (moment.isMoment(value)) {
      return value;
    }

    const parsed = moment(value);
    return parsed.isValid() ? parsed : null;
  }, []);

  const sortRepeats = React.useCallback((repeatDays = []) => {
    return [...repeatDays].sort((dayA, dayB) => daysOfWeek.indexOf(dayA) - daysOfWeek.indexOf(dayB));
  }, [daysOfWeek]);

  const getCareLabel = React.useCallback((startValue, endValue) => {
    const startMoment = toMomentOrNull(startValue);
    const endMoment = toMomentOrNull(endValue);

    if (!startMoment || !endMoment) {
      return translate('schedulestep.Common.CareLabels.Morning');
    }

    const startMeridiem = startMoment.format('A');
    const endMeridiem = endMoment.format('A');

    if (startMeridiem === 'PM' && endMeridiem === 'AM') {
      return translate('schedulestep.Common.CareLabels.Overnight');
    }

    if (startMeridiem === 'PM' && endMeridiem === 'PM') {
      return translate('schedulestep.Common.CareLabels.Evening');
    }

    return translate('schedulestep.Common.CareLabels.Morning');
  }, [toMomentOrNull]);

  const resetFromInitialEvent = React.useCallback(() => {
    if (!initialEvent) {
      setStartTime(null);
      setEndTime(null);
      setStartDate(null);
      setEndDate(null);
      setRepeats([]);
      return;
    }

    setStartTime(toMomentOrNull(initialEvent.startTime));
    setEndTime(toMomentOrNull(initialEvent.endTime));
    setStartDate(toMomentOrNull(initialEvent.startDate));
    setEndDate(toMomentOrNull(initialEvent.endDate));
    setRepeats([...initialEvent.repeats]);
  }, [initialEvent, toMomentOrNull]);

  React.useEffect(() => {
    resetFromInitialEvent();
  }, [resetFromInitialEvent]);

  const handleToggleRepeat = React.useCallback((day) => {
    setRepeats((previousRepeats) => {
      if (previousRepeats.includes(day)) {
        return previousRepeats.filter((repeatDay) => repeatDay !== day);
      }

      return [...previousRepeats, day];
    });
  }, []);

  const handleSave = React.useCallback(() => {
    if (!startTime || !endTime || !startDate || repeats.length === 0) {
      return;
    }

    const normalizedRepeats = sortRepeats(repeats);
    const nextEvent = {
      scheduleType: 'RECURRING',
      label: getCareLabel(startTime, endTime),
      startTime: startTime.toISOString(),
      endTime: endTime.toISOString(),
      repeats: normalizedRepeats,
      startDate: startDate.clone().startOf('day').toISOString(),
      endDate: endDate ? endDate.clone().startOf('day').toISOString() : null,
    };

    onSave(nextEvent);
  }, [endDate, endTime, getCareLabel, onSave, repeats, sortRepeats, startDate, startTime]);

  const isShortShift = React.useMemo(() => {
    if (!startTime || !endTime) {
      return false;
    }

    let adjustedEndTime = endTime;
    if (!endTime.isAfter(startTime)) {
      adjustedEndTime = endTime.clone().add(1, 'day');
    }

    const durationMinutes = adjustedEndTime.diff(startTime, 'minutes');
    return durationMinutes > 0 && durationMinutes <= 120;
  }, [endTime, startTime]);

  const canAddVisit = Boolean(startTime && endTime && startDate && repeats.length > 0);

  return (
    <Box
      sx={{
        border: '1px dashed #C5BBB9',
        borderRadius: '24px',
        p: '32px',
        backgroundColor: 'white',
      }}
    >
      <Stack sx={{gap: '32px'}}>
        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
          <Box sx={{display: 'flex', alignItems: 'center', gap: '12px'}}>
            <IconButton
              onClick={onRemove}
              aria-label={translate('schedulestep.ScheduleEventEditRecurring.RemoveAriaLabel')}
              sx={{
                'border': '1px solid #D4C8C2',
                'borderRadius': '8px',
                'color': '#5E5855',
                'p': '6px',
                '&:hover': {
                  borderColor: '#BDAFA8',
                  backgroundColor: '#F7F3F0',
                },
              }}
            >
              <ArrowBackIosNewRounded sx={{fontSize: 24}} />
            </IconButton>
            <Typography sx={{fontSize: '20px', fontWeight: 300, color: '#2D2925', lineHeight: '128%', fontFamily: '"Spectral", serif'}}>
              <Translate expression={'schedulestep.ScheduleEventEditRecurring.Title'} />
            </Typography>
          </Box>
          <Box sx={{border: '1px solid #C5BBB9', borderRadius: '22px', px: '16px', py: '8px'}}>
            <Typography sx={{fontSize: '14px', fontWeight: 400, color: '#3C3C3C', lineHeight: '128%', whiteSpace: 'nowrap'}}>
              <Translate expression={'schedulestep.ScheduleEventEditRecurring.StepLabel'} />
            </Typography>
          </Box>
        </Box>

        <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr'}, gap: '16px'}}>
          <Box sx={{minWidth: 0}}>
            <Typography sx={{fontSize: '16px', fontWeight: 500, color: '#2D2925', lineHeight: '150%', mb: '8px'}}>
              <Translate expression={'schedulestep.ScheduleEventEditRecurring.StartTime'} />
            </Typography>
            <TimePicker
              fullWidth
              value={startTime}
              onChange={(value) => setStartTime(toMomentOrNull(value))}
              minutesStep={30}
              timeSteps={{minutes: 30}}
              shouldDisableTime={(value, view) => view === 'minutes' && value % 30 !== 0}
              skipDisabled
              format='h:mm A'
              textFieldProps={{
                fullWidth: true,
                sx: {
                  'width': '100%',
                  'minWidth': 0,
                  '& .MuiPickersOutlinedInput-root': {
                    borderRadius: '12px',
                    fontWeight: 400,
                    fontSize: '16px',
                    lineHeight: '150%',
                  },
                  '& .MuiPickersInputBase-sectionsContainer, & .MuiPickersOutlinedInput-sectionsContainer': {
                    paddingLeft: '12px',
                  },
                  '& .MuiInputAdornment-positionEnd': {
                    marginRight: '12px',
                  },
                },
              }}
            />
          </Box>

          <Box sx={{minWidth: 0}}>
            <Typography sx={{fontSize: '16px', fontWeight: 500, color: '#2D2925', lineHeight: '150%', mb: '8px'}}>
              <Translate expression={'schedulestep.ScheduleEventEditRecurring.EndTime'} />
            </Typography>
            <TimePicker
              fullWidth
              value={endTime}
              onChange={(value) => setEndTime(toMomentOrNull(value))}
              minutesStep={30}
              timeSteps={{minutes: 30}}
              shouldDisableTime={(value, view) => view === 'minutes' && value % 30 !== 0}
              skipDisabled
              format='h:mm A'
              textFieldProps={{
                fullWidth: true,
                sx: {
                  'width': '100%',
                  '& .MuiPickersOutlinedInput-root': {
                    borderRadius: '12px',
                    fontWeight: 400,
                    fontSize: '16px',
                    lineHeight: '150%',
                  },
                  '& .MuiPickersInputBase-sectionsContainer, & .MuiPickersOutlinedInput-sectionsContainer': {
                    paddingLeft: '12px',
                  },
                  '& .MuiInputAdornment-positionEnd': {
                    marginRight: '12px',
                  },
                },
              }}
            />
          </Box>
        </Box>

        <Box>
          <Typography sx={{fontSize: '16px', fontWeight: 590, color: '#2D2925', lineHeight: '150%', mb: '8px'}}>
            <Translate expression={'schedulestep.ScheduleEventEditRecurring.Repeats'} />
          </Typography>
          <Box sx={{display: 'flex', gap: '10px', flexWrap: 'wrap'}}>
            {
              daysOfWeek.map((day) => {
                const isSelected = repeats.includes(day);
                return (
                  <Button
                    key={day}
                    onClick={() => handleToggleRepeat(day)}
                    variant='outlined'
                    sx={{
                      'minWidth': 55,
                      'borderRadius': '8px',
                      'p': '10px',
                      'fontSize': '16px',
                      'fontWeight': 590,
                      'color': isSelected ? '#FFFFFF' : '#2D2925',
                      'borderColor': '#C5BBB9',
                      'borderStyle': isSelected ? 'solid' : 'dashed',
                      'backgroundColor': isSelected ? '#DA7955' : '#0000000F',
                      '&:hover': {
                        'backgroundColor': isSelected ? '#C86945' : '#EFE8E2',
                        'borderColor': '#DA7955',
                      },
                    }}
                  >
                    {dayShortLabels[day] || day}
                  </Button>
                );
              })
            }
          </Box>
        </Box>

        <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr'}, gap: '16px'}}>
          <Box sx={{minWidth: 0}}>
            <Typography sx={{fontSize: '16px', fontWeight: 500, color: '#2D2925', lineHeight: '150%', mb: '8px'}}>
              <Translate expression={'schedulestep.ScheduleEventEditRecurring.StartDate'} />
            </Typography>
            <DatePicker
              fullWidth
              value={startDate}
              onChange={(value) => setStartDate(toMomentOrNull(value))}
              slots={{
                openPickerIcon: CalendarIcon,
              }}
              textFieldProps={{
                fullWidth: true,
                sx: {
                  'width': '100%',
                  'minWidth': 0,
                  '& .MuiPickersOutlinedInput-root': {
                    borderRadius: '12px',
                    fontWeight: 400,
                    fontSize: '16px',
                    lineHeight: '150%',
                  },
                  '& .MuiPickersInputBase-sectionsContainer, & .MuiPickersOutlinedInput-sectionsContainer': {
                    paddingLeft: '12px',
                  },
                  '& .MuiInputAdornment-positionEnd': {
                    marginRight: '12px',
                  },
                },
              }}
            />
          </Box>

          <Box sx={{minWidth: 0}}>
            <Typography sx={{fontSize: '16px', fontWeight: 500, color: '#2D2925', lineHeight: '150%', mb: '8px'}}>
              <Translate expression={'schedulestep.ScheduleEventEditRecurring.EndDateOptional'} />
            </Typography>
            <DatePicker
              fullWidth
              value={endDate}
              onChange={(value) => setEndDate(toMomentOrNull(value))}
              slots={{
                openPickerIcon: CalendarIcon,
              }}
              textFieldProps={{
                fullWidth: true,
                sx: {
                  'width': '100%',
                  'minWidth': 0,
                  '& .MuiPickersOutlinedInput-root': {
                    borderRadius: '12px',
                    fontWeight: 400,
                    fontSize: '16px',
                    lineHeight: '150%',
                  },
                  '& .MuiPickersInputBase-sectionsContainer, & .MuiPickersOutlinedInput-sectionsContainer': {
                    paddingLeft: '12px',
                  },
                  '& .MuiInputAdornment-positionEnd': {
                    marginRight: '12px',
                  },
                },
              }}
            />
          </Box>
        </Box>

        {
          isShortShift &&
          <Box sx={{p: '24px', borderRadius: '16px', backgroundColor: '#DA795514'}}>
            <Box sx={{display: 'flex', alignItems: 'center', mb: '12px', gap: '8px'}}>
              <InfoOutlined sx={{fontSize: 24, color: '#DA7955'}} />
              <Typography sx={{fontSize: '16px', fontWeight: 590, color: '#2D2925', lineHeight: '150%'}}>
                <Translate expression={'schedulestep.Common.PleaseNote'} />
              </Typography>
            </Box>
            <Typography sx={{fontSize: '16px', fontWeight: 400, color: '#2D2925', lineHeight: '150%'}}>
              <Translate expression={'schedulestep.ScheduleEventEditRecurring.ShortShiftNotice'} />
            </Typography>
          </Box>
        }

        <Box sx={{display: 'flex', justifyContent: 'flex-end', gap: '12px', flexWrap: 'wrap'}}>
          <Button
            onClick={handleSave}
            variant='contained'
            endIcon={<ArrowForwardIos />}
            disabled={!canAddVisit}
            sx={{
              'borderRadius': '12px',
              'textTransform': 'none',
              'backgroundColor': '#DA7955',
              'fontSize': '16px',
              'fontWeight': 500,
              'lineHeight': '150%',
              'color': '#F6F2EF',
              'px': '20px',
              'py': '14px',
              'gap': '4px',
              '&:hover': {backgroundColor: '#C86945'},
            }}
          >
            <Translate expression={'schedulestep.ScheduleEventEditRecurring.AddVisitButton'} />
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

ScheduleEventEditRecurring.propTypes = {
  initialEvent: PropTypes.shape({
    startTime: PropTypes.string,
    endTime: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    repeats: PropTypes.arrayOf(PropTypes.string),
  }),
  onSave: PropTypes.func,
  onRemove: PropTypes.func,
};

export default React.memo(ScheduleEventEditRecurring);
